import { describeError } from '@/lib/errors/unhandledErrors';

import { collectBrowserInfo } from './browserInfo';
import type { BrowserInfo } from './browserInfo';
import { getConsoleEntries, getDroppedConsoleEntryCount, isConsoleCaptureInstalled } from './consoleCapture';
import { redactSensitiveText, redactUrl } from './redaction';
import { collectRecentResourceTimings, collectRuntimeInfo } from './runtimeInfo';
import type { ResourceTimingInfo, RuntimeInfo } from './runtimeInfo';
import { getSessionInfo } from './sessionInfo';
import type { SessionInfo } from './sessionInfo';

export type DiagnosticReportOptions = {
  error?: unknown;
  errorOccurredAt?: string;
  errorPageUrl?: string;
  generatedAt?: Date;
};

const TITLE = 'NG-SOS Portal error report';

function line(label: string, value: string | number | undefined): string | undefined {
  return value === undefined || value === '' ? undefined : `${label}: ${value}`;
}

function yesNo(value: boolean): string {
  return value ? 'yes' : 'no';
}

function section(title: string, lines: (string | undefined)[]): string {
  const body = lines.filter((entry): entry is string => entry !== undefined);
  return `## ${title}\n${body.length > 0 ? body.join('\n') : '(nothing captured)'}`;
}

function pageSection(): string {
  return section('Page', [
    line('URL', redactUrl(window.location.href)),
    line('Referrer', document.referrer === '' ? undefined : redactUrl(document.referrer)),
    line('Title', document.title),
  ]);
}

function sessionSection(session: SessionInfo): string {
  if (!session.authenticated) {
    return section('Session', ['Signed in: no']);
  }

  return section('Session', [
    'Signed in: yes',
    line('Username', session.username),
    line('Agency id', session.agencyId),
    line('Subject', session.subject),
    line('Roles', session.roles.length > 0 ? session.roles.join(', ') : undefined),
    line('Issuer', session.issuer),
    line('Token expires at', session.expiresAt),
  ]);
}

function browserSection(browser: BrowserInfo | undefined): string {
  if (browser === undefined) {
    return section('Browser', []);
  }

  return section('Browser', [
    line('Browser', browser.browser),
    line('Engine', browser.engine),
    line('OS', browser.os),
    line('Platform', browser.platform),
    line('Architecture', browser.architecture),
    line('User agent', browser.userAgent),
    line('Languages', browser.languages),
    line('Time zone', browser.timezone),
    line('Viewport', browser.viewport),
    line('Screen', browser.screen),
    line('Online', yesNo(browser.online)),
    line('Cookies enabled', yesNo(browser.cookiesEnabled)),
    line('Logical CPUs', browser.logicalCpus),
    line('Device memory', browser.deviceMemoryGb === undefined ? undefined : `${browser.deviceMemoryGb} GB`),
    line('Max touch points', browser.maxTouchPoints),
  ]);
}

function milliseconds(value: number | undefined): string | undefined {
  return value === undefined ? undefined : `${value} ms`;
}

function runtimeSection(runtime: RuntimeInfo | undefined): string {
  if (runtime === undefined) {
    return section('Runtime', []);
  }

  const scriptLines =
    runtime.scripts.length === 0
      ? ['Loaded scripts: (none captured)']
      : [`Loaded scripts (${runtime.scripts.length}):`, ...runtime.scripts.map((script) => `- ${script}`)];

  return section('Runtime', [
    line('Document state', runtime.documentReadyState),
    line('Visibility', runtime.visibilityState),
    line('Window focused', yesNo(runtime.windowFocused)),
    line('Page lifetime', milliseconds(runtime.pageLifetimeMs)),
    line('Navigation type', runtime.navigationType),
    line('DOM content loaded', milliseconds(runtime.domContentLoadedMs)),
    line('Load event', milliseconds(runtime.loadEventMs)),
    line('Service worker controller', runtime.serviceWorker ?? 'none'),
    ...scriptLines,
  ]);
}

function formatResourceTiming(entry: ResourceTimingInfo): string {
  const details = [
    entry.initiatorType,
    entry.responseStatus === undefined ? undefined : `HTTP ${entry.responseStatus}`,
    `${entry.durationMs} ms`,
    entry.transferSize === undefined ? undefined : `${entry.transferSize} B`,
  ].filter((value): value is string => value !== undefined);

  const timestamp = entry.timestamp === undefined ? '' : `[${entry.timestamp}] `;
  return `${timestamp}${details.join(', ')} ${entry.url}`;
}

function resourceTimingSection(): string {
  const resources = collectRecentResourceTimings();
  const droppedNote = resources.dropped > 0 ? `, ${resources.dropped} older omitted` : '';
  const header = `## Recent resources (${resources.entries.length} entries${droppedNote}, newest last)`;

  return resources.entries.length === 0
    ? `${header}\n(nothing captured or Resource Timing unavailable)`
    : `${header}\n${resources.entries.map(formatResourceTiming).join('\n')}`;
}

function consoleSection(): string {
  if (!isConsoleCaptureInstalled()) {
    return '## Console\n(console capture is not installed)';
  }

  const entries = getConsoleEntries();
  const dropped = getDroppedConsoleEntryCount();
  const droppedNote = dropped > 0 ? `, ${dropped} older dropped` : '';
  const header = `## Console (${entries.length} entries${droppedNote}, newest last)`;

  if (entries.length === 0) {
    return `${header}\n(nothing logged)`;
  }

  const body = entries
    .map((entry) => `[${entry.timestamp}] ${entry.level.toUpperCase().padEnd(5)} ${entry.message}`)
    .join('\n');

  return `${header}\n${body}`;
}

function collectBrowserInfoOrNothing(): BrowserInfo | undefined {
  try {
    return collectBrowserInfo();
  } catch {
    return undefined;
  }
}

function collectRuntimeInfoOrNothing(): RuntimeInfo | undefined {
  try {
    return collectRuntimeInfo();
  } catch {
    return undefined;
  }
}

function errorSection(options: DiagnosticReportOptions): string | undefined {
  if (options.error === undefined) {
    return undefined;
  }

  return section('Error', [
    line('Occurred at', options.errorOccurredAt),
    line('Page at error', options.errorPageUrl === undefined ? undefined : redactUrl(options.errorPageUrl)),
    `Details:\n${redactSensitiveText(describeError(options.error))}`,
  ]);
}

export function buildDiagnosticReport(options: DiagnosticReportOptions = {}): string {
  const generatedAt = options.generatedAt ?? new Date();
  const browser = collectBrowserInfoOrNothing();
  const runtime = collectRuntimeInfoOrNothing();

  const sections = [
    `${TITLE}\nGenerated at ${generatedAt.toISOString()}`,
    errorSection(options),
    pageSection(),
    sessionSection(getSessionInfo()),
    browserSection(browser),
    runtimeSection(runtime),
    resourceTimingSection(),
    consoleSection(),
  ];

  return sections.filter((entry): entry is string => entry !== undefined).join('\n\n');
}
